import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from '../auth/useLocalStorage';

interface ThemeColors {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  background: string;
  backgroundLight: string;
  backgroundDark: string;
  text: string;
  textLight: string;
  border: string;
  shadow: string;
  gradient: string;
  badgeColor: string;
  areaName: string;
  areaIcon: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  isKitchen: boolean;
  isSalesFloor: boolean;
  areaName: string;
}

const kitchenTheme: ThemeColors = {
  primary: '#dc3545',
  primaryDark: '#c82333',
  primaryLight: '#f5c6cb',
  secondary: '#fd7e14',
  background: '#fff5f5',
  backgroundLight: '#ffeaea',
  backgroundDark: '#ffe0e0',
  text: '#721c24',
  textLight: '#856404',
  border: '#f1aeb5',
  shadow: 'rgba(220, 53, 69, 0.15)',
  gradient: 'linear-gradient(135deg, #fff5f5, #ffeaea)',
  badgeColor: '#dc3545',
  areaName: 'COCINA',
  areaIcon: '🍳'
};

const salesFloorTheme: ThemeColors = {
  primary: '#28a745',
  primaryDark: '#1e7e34',
  primaryLight: '#c3e6cb',
  secondary: '#17a2b8',
  background: '#ffffff',
  backgroundLight: '#f8f9fa',
  backgroundDark: '#e9ecef',
  text: '#212529',
  textLight: '#6c757d',
  border: '#28a745',
  shadow: 'rgba(40, 167, 69, 0.15)',
  gradient: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  badgeColor: '#28a745',
  areaName: 'PISO DE VENTA',
  areaIcon: '🏪'
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [user] = useLocalStorage('user', null);
  const [colors, setColors] = useState<ThemeColors>(kitchenTheme);

  const isKitchen = location.pathname === '/';
  const isSalesFloor = location.pathname === '/sales-floor';

  useEffect(() => {
    // Solo aplicar tema personalizado para piso de venta
    if (isSalesFloor) {
      const newColors = salesFloorTheme;
      setColors(newColors);

      // Aplicar variables CSS globales solo para piso de venta
      const root = document.documentElement;
      root.style.setProperty('--theme-primary', newColors.primary);
      root.style.setProperty('--theme-primary-dark', newColors.primaryDark);
      root.style.setProperty('--theme-primary-light', newColors.primaryLight);
      root.style.setProperty('--theme-secondary', newColors.secondary);
      root.style.setProperty('--theme-background', newColors.background);
      root.style.setProperty('--theme-background-light', newColors.backgroundLight);
      root.style.setProperty('--theme-background-dark', newColors.backgroundDark);
      root.style.setProperty('--theme-text', newColors.text);
      root.style.setProperty('--theme-text-light', newColors.textLight);
      root.style.setProperty('--theme-border', newColors.border);
      root.style.setProperty('--theme-shadow', newColors.shadow);
      root.style.setProperty('--theme-gradient', newColors.gradient);

      // Aplicar clase al body solo para piso de venta
      document.body.className = 'theme-sales-floor';
    } else {
      // Para cocina, usar colores por defecto y limpiar el tema
      setColors(kitchenTheme);
      document.body.className = '';

      // Limpiar variables CSS para que cocina use estilos originales
      const root = document.documentElement;
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-primary-dark');
      root.style.removeProperty('--theme-primary-light');
      root.style.removeProperty('--theme-secondary');
      root.style.removeProperty('--theme-background');
      root.style.removeProperty('--theme-background-light');
      root.style.removeProperty('--theme-background-dark');
      root.style.removeProperty('--theme-text');
      root.style.removeProperty('--theme-text-light');
      root.style.removeProperty('--theme-border');
      root.style.removeProperty('--theme-shadow');
      root.style.removeProperty('--theme-gradient');
    }
  }, [location.pathname, isSalesFloor]);

  const areaName = isSalesFloor ? 'PISO DE VENTA' : 'COCINA';

  return (
    <ThemeContext.Provider value={{ colors, isKitchen, isSalesFloor, areaName }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
